import React, { useEffect, useRef, useState } from 'react';
import { trackEvent } from '../analytics';

const CAL_LINK = 'apnaaapan/30min';
const CAL_URL = `https://cal.com/${CAL_LINK}`;
const BRAND_ORANGE = '#F26B2A';
const BRAND_ORANGE_DARK = '#FF7A3D';

const isDarkTheme = () => document.documentElement.classList.contains('dark');

function applyCalTheme(api, dark) {
  api('ui', {
    theme: dark ? 'dark' : 'light',
    cssVarsPerTheme: {
      light: { 'cal-brand': BRAND_ORANGE },
      dark: { 'cal-brand': BRAND_ORANGE_DARK },
    },
    hideEventTypeDetails: false,
    layout: 'month_view',
  });
}

// Official Cal.com embed loader: defines window.Cal and queues calls until embed.js loads.
function ensureCalLoader() {
  if (window.Cal) return;
  (function (C, A, L) {
    const p = function (a, ar) { a.q.push(ar); };
    const d = C.document;
    C.Cal = C.Cal || function () {
      const cal = C.Cal;
      const ar = arguments;
      if (!cal.loaded) {
        cal.ns = {};
        cal.q = cal.q || [];
        d.head.appendChild(d.createElement('script')).src = A;
        cal.loaded = true;
      }
      if (ar[0] === L) {
        const api = function () { p(api, arguments); };
        const namespace = ar[1];
        api.q = api.q || [];
        if (typeof namespace === 'string') {
          cal.ns[namespace] = cal.ns[namespace] || api;
          p(cal.ns[namespace], ar);
          p(cal, ['initNamespace', namespace]);
        } else {
          p(cal, ar);
        }
        return;
      }
      p(cal, ar);
    };
  })(window, 'https://app.cal.com/embed/embed.js', 'init');
}

/**
 * Inline Cal.com booking calendar that follows the site theme (light / dark, live).
 * Fires `book_call` (GA4) / `Schedule` (Meta) when a booking completes.
 */
const CalEmbed = ({ namespace, location = 'page', className = '' }) => {
  const containerRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const elementId = `cal-inline-${namespace}`;

  useEffect(() => {
    ensureCalLoader();
    const Cal = window.Cal;
    if (!Cal.ns || !Cal.ns[namespace]) {
      Cal('init', namespace, { origin: 'https://cal.com' });
    }
    const api = Cal.ns[namespace];
    const container = containerRef.current;

    // Guard against double mounting into the same element (React StrictMode re-runs effects).
    if (container && !container.dataset.calMounted) {
      container.dataset.calMounted = 'true';
      api('inline', {
        elementOrSelector: `#${elementId}`,
        calLink: CAL_LINK,
        config: { layout: 'month_view', theme: isDarkTheme() ? 'dark' : 'light' },
      });
    }
    applyCalTheme(api, isDarkTheme());

    // Follow the header theme toggle while the calendar is on screen.
    let lastDark = isDarkTheme();
    const themeObserver = new MutationObserver(() => {
      const dark = isDarkTheme();
      if (dark !== lastDark) {
        lastDark = dark;
        applyCalTheme(api, dark);
      }
    });
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    if (!window.__apnCalListeners) window.__apnCalListeners = {};
    if (!window.__apnCalListeners[namespace]) {
      window.__apnCalListeners[namespace] = true;
      api('on', {
        action: 'bookingSuccessfulV2',
        callback: () => trackEvent('book_call', { cta_location: location }, 'Schedule'),
      });
    }

    // The embed injects an iframe into the container once it is ready.
    const observer = new MutationObserver(() => {
      if (containerRef.current && containerRef.current.querySelector('iframe')) {
        setLoaded(true);
        observer.disconnect();
      }
    });
    if (containerRef.current) {
      observer.observe(containerRef.current, { childList: true, subtree: true });
    }

    // If the script is blocked (ad blockers, network), offer a direct link instead of an empty box.
    const timeout = setTimeout(() => {
      if (!containerRef.current || !containerRef.current.querySelector('iframe')) {
        setFailed(true);
      }
    }, 10000);

    return () => {
      observer.disconnect();
      themeObserver.disconnect();
      clearTimeout(timeout);
    };
  }, [namespace, elementId, location]);

  return (
    <div className={`relative ${className}`}>
      {!loaded && !failed && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-apn-muted" aria-live="polite">
          <div className="w-10 h-10 rounded-full border-4 border-apn-line border-t-apn-orange animate-spin motion-reduce:animate-none" />
          <p className="text-sm">Loading available slots…</p>
        </div>
      )}
      {failed && !loaded && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center">
          <p className="text-apn-text">The calendar didn't load here.</p>
          <a
            href={CAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-cta={`${location}-cal-fallback`}
            className="bg-gradient-to-r from-orange-500 to-yellow-400 text-white px-6 py-3 rounded-full font-semibold"
          >
            Open the booking page
          </a>
          <a
            href="https://wa.me/919587773274?text=Hi%20Apnaaapan%2C%20your%20booking%20calendar%20didn%27t%20load%20for%20me.%20Can%20we%20fix%20a%20time%20for%20a%20strategy%20call%3F"
            data-cta={`${location}-cal-fallback-whatsapp`}
            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-[#0B2E17] px-5 py-2.5 text-sm font-semibold"
          >
            Or fix a time on WhatsApp
          </a>
        </div>
      )}
      <div id={elementId} ref={containerRef} className="w-full h-full overflow-auto" />
    </div>
  );
};

export default CalEmbed;
